// A dropdown menu anchored to an AppIconButtonGroup item. The DS `Dropdown`
// renders its own AppButton trigger, so it can't wrap an icon-group button;
// instead this composes the DS `MenuList` + `ListItem` into a popover.
//
// Positioning: the menu is portaled INTO the nearest scroll container (not
// <body>) and positioned `absolute` in that container's content coordinates.
// This means the browser scrolls the menu natively in lockstep with the group —
// no JS-on-scroll repositioning (which trails the compositor and visibly
// "dances"), and the container's own overflow clips the menu below the sticky
// nav/header instead of letting it ride over them. Escaping the group's
// `overflow-clip` / the section card's `overflow-hidden` is automatic because
// the portal hops straight to the scroll container.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { AppIconButtonGroupItem, ListItem } from '@clickguard/design-system'
import { usePrefersReducedMotion } from './customize-reveal'
import { acquirePositioned, releasePositioned } from 'components/data-table-v2'

export interface IconMenuItem {
	label: ReactNode
	leadingIcon?: ReactNode
	onClick?: () => void
	disabled?: boolean
	selected?: boolean
}

/** A titled cluster of options. When `sections` is passed the flat `items` are
 *  ignored and the menu renders one labeled group per section (Figma section
 *  headers), scrolling as a whole when the list overflows the height cap. */
export interface IconMenuSection {
	title?: ReactNode
	items: IconMenuItem[]
}

// Figma "Shadows/shadow-lg" (node 6350:135101) — layered drop shadow.
const SHADOW_LG = '0px 12px 16px -4px rgba(16,24,40,0.08), 0px 4px 6px -2px rgba(16,24,40,0.03)'

// Nearest ancestor that actually scrolls; falls back to the document element.
const getScrollParent = (el: HTMLElement | null): HTMLElement => {
	let node = el?.parentElement ?? null
	while (node) {
		const cs = getComputedStyle(node)
		if (/(auto|scroll)/.test(cs.overflowY) && node.scrollHeight > node.clientHeight) return node
		node = node.parentElement
	}
	return document.scrollingElement as HTMLElement
}

export const IconMenu = ({
	icon,
	ariaLabel,
	title,
	header,
	items,
	sections,
	contentKey,
	menuWidth = 240,
	menuMaxHeight = 380
}: {
	icon: ReactNode
	ariaLabel: string
	/** Heading shown in the menu's title section (Figma 6350:135101). */
	title?: ReactNode
	/** Custom pinned header, rendered raw in the title slot instead of `title`.
	 *  Use it when the header is a control (the Aggregate by / Stack by tabs)
	 *  rather than a line of text. */
	header?: ReactNode
	/** Flat option list. Ignored when `sections` is provided. */
	items?: IconMenuItem[]
	/** Grouped options — renders a labeled header per section. */
	sections?: IconMenuSection[]
	/** Changes when the header swaps the whole option list for another one, so
	 *  the rows crossfade instead of cutting. Leave unset for a static list. */
	contentKey?: string
	menuWidth?: number
	/** Cap for the scrollable options region (the title stays pinned above it). */
	menuMaxHeight?: number
}) => {
	const reduced = usePrefersReducedMotion()
	const [open, setOpen] = useState(false)
	const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	// The scroll container we portal into. Flipped to `position: relative` via a
	// shared ref-counted lease (positioned-lease.ts) so many popovers can share one
	// container without clobbering each other's anchor or leaking the mutation.
	const scRef = useRef<HTMLElement | null>(null)
	const heldRef = useRef(false)
	// Mirror `open` in a ref so onExitComplete / unmount cleanup read the latest
	// value without being pinned to a stale render closure.
	const openRef = useRef(open)
	openRef.current = open

	const acquireLease = (sc: HTMLElement) => {
		if (heldRef.current) return
		acquirePositioned(sc)
		heldRef.current = true
	}
	const releaseLease = () => {
		if (!heldRef.current) return
		const sc = scRef.current
		if (sc) releasePositioned(sc)
		heldRef.current = false
	}

	const place = useCallback(() => {
		const group = (triggerRef.current?.closest('[role="group"]') as HTMLElement) ?? triggerRef.current
		const sc = scRef.current
		if (!group || !sc) return
		const gr = group.getBoundingClientRect()
		const sr = sc.getBoundingClientRect()
		// Group position expressed in the container's scrollable content space, so
		// the absolutely-positioned menu rides the content as it scrolls.
		const top = gr.bottom - sr.top + sc.scrollTop + 4
		const left = Math.max(8, gr.right - sr.left + sc.scrollLeft - menuWidth)
		setPos({ top: Math.round(top), left: Math.round(left) })
	}, [menuWidth])

	const toggle = () => {
		if (open) {
			setOpen(false)
			return
		}
		const sc = getScrollParent(triggerRef.current)
		scRef.current = sc
		// Take a lease so the container is a containing block for the absolute menu.
		acquireLease(sc)
		// NOTE: do NOT place() here. Positioning happens in the layout effect below
		// so the FIRST painted frame is already at the anchor. Measuring in the click
		// handler leaves the previous open's stale `pos` on screen for a frame (the
		// menu "jumps from elsewhere, then snaps"); useLayoutEffect runs after the
		// open render but before paint, so the correct position is committed first.
		setOpen(true)
	}

	// Measure + position the moment `open` flips true (or menuWidth changes while
	// open), before the browser paints.
	useLayoutEffect(() => {
		if (open) place()
	}, [open, place])

	useEffect(() => {
		if (!open) return
		const onDoc = (e: MouseEvent) => {
			const t = e.target as Node
			if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return
			setOpen(false)
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setOpen(false)
				triggerRef.current?.focus()
			}
		}
		// No scroll handler needed — the menu is in the scroll container's content
		// and moves with it natively. Recompute only on resize (layout reflow).
		const onResize = () => place()
		document.addEventListener('mousedown', onDoc)
		document.addEventListener('keydown', onKey)
		window.addEventListener('resize', onResize)
		return () => {
			document.removeEventListener('mousedown', onDoc)
			document.removeEventListener('keydown', onKey)
			window.removeEventListener('resize', onResize)
		}
	}, [open, place])

	// Release the lease if we unmount while still open (AnimatePresence is torn
	// down with us, so its onExitComplete never fires — prevents a leaked
	// `position: relative` on the shared container).
	useEffect(() => () => releaseLease(), [])

	const renderItem = (it: IconMenuItem, i: number) => (
		<ListItem
			key={i}
			label={it.label}
			leadingIcon={it.leadingIcon}
			selected={it.selected}
			disabled={it.disabled}
			onClick={() => {
				if (it.disabled) return
				it.onClick?.()
				setOpen(false)
			}}
		/>
	)

	return (
		<>
			<AppIconButtonGroupItem
				ref={triggerRef}
				aria-label={ariaLabel}
				aria-haspopup="menu"
				aria-expanded={open}
				icon={icon}
				onClick={toggle}
			/>
			{pos &&
				scRef.current &&
				createPortal(
					// Grow OUT of the trigger corner: the menu is right-aligned under the
					// button, so a scale pivoted at 'top right' (the button corner) and
					// scaling up from 0.9 unfurls the menu from the button itself. No `y`
					// translate: a slide reads as dropping in from above ("from somewhere
					// else"), not expanding from the anchor. Snappy ease-out-expo.
					<AnimatePresence
						// Release the lease ONLY after the exit animation finishes —
						// restoring the container mid-exit (while the menu is still mounted
						// and absolute) swaps its containing block, re-resolving top/left
						// against a new origin so it visibly jumps left+down before fading.
						// Guarded by openRef: if reopened during the exit, keep the lease.
						onExitComplete={() => {
							if (!openRef.current) releaseLease()
						}}
					>
						{open && (
							<motion.div
								ref={menuRef}
								// z-[5]: the menu belongs to the scrolled content, so it sits BELOW
								// the sticky page header (z-10) and navbar (z-20) — on scroll it
								// slides under the chrome instead of riding over it (house rule,
								// see canvas-header.tsx). z-50 rode over the header.
								className="absolute z-[5] flex max-h-[85vh] flex-col overflow-clip rounded-xl border border-[rgb(var(--cg-border))] bg-white"
								style={{ top: pos.top, left: pos.left, width: menuWidth, boxShadow: SHADOW_LG, transformOrigin: 'top right' }}
								initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
								animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1 }}
								exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.94 }}
								transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
							>
								{/* Title section — Medium 14/18, full-bleed bottom divider. Pinned:
								    stays put while the options below scroll.
								    A custom `header` gets no padding and no border of its own: it is a
								    DS TabList, which draws the divider itself as its inactive baseline
								    and needs the full width to reach both edges. A padded wrapper would
								    inset that line and a second border would double it. */}
								{header != null ? (
									<div className="shrink-0">{header}</div>
								) : (
									title != null && (
										<div className="shrink-0 border-b border-[rgb(var(--cg-border))] p-1">
											<div className="px-2 py-2 text-sm font-medium leading-[18px] text-[rgb(var(--cg-text-primary))]">{title}</div>
										</div>
									)
								)}
								{/* Options — DS ListItem rows (selected renders the brand check).
								    Scrolls on overflow so a long metric list stays usable. */}
								<div className="min-h-0 overflow-y-auto" style={{ maxHeight: menuMaxHeight }}>
									{/* Crossfade when `contentKey` changes — the pinned header can swap
									    the entire option list (Aggregate by → Stack by), and the rows
									    must not cut from one set to the other. mode="wait" plus
									    initial={false} keeps the menu's own open animation untouched. */}
									<AnimatePresence mode="wait" initial={false}>
										<motion.div
											key={contentKey ?? 'options'}
											initial={reduced ? false : { opacity: 0 }}
											animate={{ opacity: 1 }}
											exit={{ opacity: 0 }}
											transition={{ duration: reduced ? 0 : 0.12, ease: 'linear' }}
										>
											{sections ? (
												sections.map((sec, si) => (
													<div key={si} className="flex flex-col gap-0.5 p-1">
														{sec.title != null && (
															<div className="px-2 pb-1 pt-1.5 text-xs font-medium uppercase tracking-wide text-[#8b8a8f]">
																{sec.title}
															</div>
														)}
														{sec.items.map(renderItem)}
													</div>
												))
											) : (
												<div className="flex flex-col gap-0.5 p-1">{(items ?? []).map(renderItem)}</div>
											)}
										</motion.div>
									</AnimatePresence>
								</div>
							</motion.div>
						)}
					</AnimatePresence>,
					scRef.current
				)}
		</>
	)
}
